import FormatCurrency from 'components/Common/FormatCurrency'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import LuxPlusLogoWrapper from 'luxPlus/components/LuxPlusLogoWrapper'
import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'

type SaleUnitDisplay = 'text' | 'slash'
type NonMemberDisplay = 'plain' | 'bold'

interface Props {
  points: number
  cash: number
  nonMemberCash?: number
  isMemberReward: boolean
  saleUnit: string
  saleUnitDisplay: SaleUnitDisplay
  nonMemberDisplay: NonMemberDisplay
  testId: string
  showLuxPlusLogo?: boolean
}

function RewardBundlePriceBlock({
  points,
  cash,
  nonMemberCash,
  isMemberReward,
  saleUnit,
  saleUnitDisplay,
  nonMemberDisplay,
  testId,
  showLuxPlusLogo,
}: Props) {
  const hasSaleUnit = saleUnit.length > 0

  return (
    <VerticalSpacer gap={4}>
      <Group direction="horizontal" gap={4} verticalAlign="baseline" wrap="wrap" data-testid={testId}>
        <Heading variant="heading4" colour={isMemberReward ? 'lux-plus' : undefined}>
          <FormattedMessage
            id="O51UqWfnjmACLasEFZUl"
            defaultMessage="{points} + {cash}"
            description="Reward bundle price on offer cards, showing the loyalty points portion and the cash portion."
            values={{
              points: <FormatLoyaltyPoints points={points} abbreviated />,
              cash: <FormatCurrency value={cash} format="roundedDollar" />,
            }}
          />
        </Heading>
        {saleUnitDisplay === 'text' && hasSaleUnit && <Caption variant="medium" colour={isMemberReward ? 'lux-plus' : 'neutral-three'} weight="normal">
          {saleUnit}
        </Caption>}
        {saleUnitDisplay === 'slash' && isMemberReward && showLuxPlusLogo && hasSaleUnit && <Caption variant="medium" colour="lux-plus" weight="normal">
          <FormattedMessage
            id="F30l6DxoJl2-QGoduLrS"
            defaultMessage="/{saleUnit} with <luxPlusLogo></luxPlusLogo>"
            description="Sale unit suffix for LuxPlus reward preview pricing on the Société Rewards offer carousel."
            values={{
              saleUnit,
              luxPlusLogo: () => <LuxPlusLogoWrapper height={8} colour="inherit" />,
            }}
          />
        </Caption>}
        {saleUnitDisplay === 'slash' && isMemberReward && showLuxPlusLogo && !hasSaleUnit && <Caption variant="medium" colour="lux-plus" weight="normal" format="lowercase" wrap="no-wrap">
          <FormattedMessage
            id="qg18mjM_yahXRtEZVoVD"
            defaultMessage="with <luxPlusLogo></luxPlusLogo>"
            description="Caption suffix appended after a LuxPlus member price that reads with the LuxPlus logo"
            values={{
              luxPlusLogo: () => <LuxPlusLogoWrapper height={8} colour="inherit" />,
            }}
          />
        </Caption>}
        {saleUnitDisplay === 'slash' && hasSaleUnit && (!isMemberReward || !showLuxPlusLogo) && <Caption variant="medium" colour={isMemberReward ? 'lux-plus' : 'neutral-three'} weight="normal">
          <FormattedMessage
            id="uBDED9yQTDSyunOfEF4g"
            defaultMessage="/{saleUnit}"
            description="Sale unit suffix for reward preview pricing on the Société Rewards offer carousel."
            values={{ saleUnit }}
          />
        </Caption>}
      </Group>
      {isMemberReward && nonMemberCash !== undefined && nonMemberDisplay === 'plain' && (
        <Caption variant="medium" colour="neutral-two">
          <FormattedMessage
            id="4En0A1CxkiHl1JAZ5Urp"
            defaultMessage="Non-member {points} + {cash}"
            description="Non-member reward price caption shown below the LuxPlus member price on the main offer card; combines loyalty points portion with cash co-pay."
            values={{
              points: <b><FormatLoyaltyPoints points={points} abbreviated /></b>,
              cash: <b><FormatCurrency value={nonMemberCash} format="roundedDollar" /></b>,
            }}
          />
        </Caption>
      )}
      {isMemberReward && nonMemberCash !== undefined && nonMemberDisplay === 'bold' && (
        <Caption variant="medium" colour="neutral-two">
          <FormattedMessage
            id="7mMEy_MsCPBFL0TGkYOh"
            defaultMessage="Non-member <b>{points} + {cash}</b>"
            description="Non-member reward preview price shown below the LuxPlus member price on the Société Rewards offer carousel."
            values={{
              points: <FormatLoyaltyPoints points={points} abbreviated />,
              cash: <FormatCurrency value={nonMemberCash} format="roundedDollar" />,
              b: (chunks: ReactNode) => <b>{chunks}</b>,
            }}
          />
        </Caption>
      )}
    </VerticalSpacer>
  )
}

export default RewardBundlePriceBlock
